<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from 'vue';

interface PanelFieldDef {
  key: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    fields: PanelFieldDef[];
    hidden: string[];
    labelWidth: number;
    layoutEditMode?: boolean;
    minLabelWidth?: number;
    maxLabelWidth?: number;
  }>(),
  {
    minLabelWidth: 64,
    maxLabelWidth: 480,
  },
);

const emit = defineEmits<{
  'update:labelWidth': [value: number];
}>();

const container = useTemplateRef<HTMLElement>('container');
let stopDrag: (() => void) | null = null;

function isVisible(key: string): boolean {
  return !props.hidden.includes(key);
}

function clamp(value: number): number {
  return Math.min(Math.max(value, props.minLabelWidth), props.maxLabelWidth);
}

function startResize(event: MouseEvent) {
  const element = container.value;
  if (!element) {
    return;
  }

  const originLeft = element.getBoundingClientRect().left;

  const onMove = (moveEvent: MouseEvent) => {
    emit('update:labelWidth', clamp(Math.round(moveEvent.clientX - originLeft)));
  };
  const onUp = () => {
    stopDrag?.();
    stopDrag = null;
  };

  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp, { once: true });
  stopDrag = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };
}

onBeforeUnmount(() => {
  stopDrag?.();
});
</script>

<template>
  <div
    ref="container"
    class="panel-field-grid"
    :style="{ '--panel-label-width': `${labelWidth}px` }"
  >
    <template v-for="field in fields" :key="field.key">
      <div v-show="isVisible(field.key)" class="panel-field">
        <div class="panel-field-label">
          <label class="panel-field-label-text">{{ field.label }}</label>
          <slot :name="`label-${field.key}`"></slot>
        </div>
        <div class="panel-field-control"><slot :name="field.key"></slot></div>
      </div>
    </template>

    <div
      v-if="layoutEditMode"
      class="field-splitter"
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize labels"
      @mousedown.prevent="startResize"
    ></div>
  </div>
</template>

<style scoped>
.panel-field-grid {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.panel-field {
  display: grid;
  grid-template-columns: var(--panel-label-width, 11rem) minmax(0, 1fr);
  gap: 0.75rem;
  align-items: center;
}

.panel-field-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.panel-field-label-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-field-control {
  min-width: 0;
}

/* Stretch inputs to fill the control column, but leave toggle/checkbox/color
   controls at their intrinsic PrimeVue width — overriding it with `width: auto`
   collapses the switch (its width comes from a CSS width token, not content). */
.panel-field-control :deep(.p-component:not(.p-toggleswitch):not(.p-checkbox):not(.p-colorpicker)) {
  width: 100%;
}

.field-splitter {
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(var(--panel-label-width) + 0.375rem);
  width: 8px;
  margin-left: -4px;
  cursor: col-resize;
  z-index: 2;
}

.field-splitter::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 3px;
  width: 2px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--p-content-border-color) 70%, transparent);
}

.field-splitter:hover::after {
  background: color-mix(in srgb, var(--p-primary-color) 60%, transparent);
}
</style>
